import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { LotMonitoring } from '../types'
import { fmtDate, fmtMoney } from '../lib/format'

export default function LotsTab() {
  const [lots, setLots] = useState<LotMonitoring[]>([])
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<LotMonitoring>>({})
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    refresh()
  }, [showAll])

  async function refresh() {
    try {
      const list = await api.lots.list(showAll ? {} : { qualifies: true })
      setLots(list)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  async function add() {
    if (!draft.address) return
    await api.lots.create(draft)
    setDraft({})
    setAdding(false)
    await refresh()
  }
  async function draftLetter(id: string) {
    setBusyId(id)
    try {
      await api.lots.draftLetter(id)
      await refresh()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusyId(null)
    }
  }
  async function sendLetter(id: string) {
    const recipient = prompt('Recipient email:')
    if (!recipient) return
    setBusyId(id)
    try {
      await api.lots.sendLetter(id, recipient)
      await refresh()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Lot Monitoring</h2>
          <p className="muted small">
            Qualifying lot sales in Heritage's target geography. Manual entry until automated
            Washington County scraper is wired (see CREDENTIALS_NEEDED).
          </p>
        </div>
        <div className="row gap-2">
          <label className="small">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />{' '}
            Include disqualified
          </label>
          <button className="btn-primary" onClick={() => setAdding(!adding)}>
            {adding ? 'Cancel' : '+ Add lot'}
          </button>
        </div>
      </div>

      {error && <div className="card alert mb-2">{error}</div>}

      {adding && (
        <div className="card mb-3">
          <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
            <input placeholder="Address" value={draft.address ?? ''} onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
            <input placeholder="City" value={draft.city ?? ''} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
            <input placeholder="Acreage" type="number" value={(draft.acreage as number | undefined) ?? ''} onChange={(e) => setDraft({ ...draft, acreage: parseFloat(e.target.value) || null })} />
            <input placeholder="Sale price" type="number" value={(draft.sale_price as number | undefined) ?? ''} onChange={(e) => setDraft({ ...draft, sale_price: parseFloat(e.target.value) || null })} />
            <input placeholder="Sale date" type="date" value={draft.sale_date ?? ''} onChange={(e) => setDraft({ ...draft, sale_date: e.target.value })} />
            <input placeholder="Buyer name" value={draft.buyer_name ?? ''} onChange={(e) => setDraft({ ...draft, buyer_name: e.target.value })} />
          </div>
          <button className="btn-primary mt-2" onClick={add}>
            Add lot
          </button>
        </div>
      )}

      {lots.length === 0 ? (
        <p className="muted">No lots tracked yet.</p>
      ) : (
        lots.map((l) => (
          <div
            key={l.id}
            className="card mb-2"
            style={{ borderLeft: l.qualifies ? '3px solid #2ea043' : '3px solid #666' }}
          >
            <div className="row-between">
              <div style={{ flex: 1 }}>
                <div className="row-title">{l.address}</div>
                <div className="muted small">
                  {l.city ?? ''} · {l.acreage ?? '?'} acres · {l.sale_price ? fmtMoney(l.sale_price) : 'price unknown'} · sold {fmtDate(l.sale_date)}
                </div>
                {l.buyer_name && (
                  <div className="muted small">Buyer: {l.buyer_name}</div>
                )}
                {!l.qualifies && (
                  <div className="muted small mt-1">
                    Disqualified: {l.qualifying_reasoning ?? 'unknown reason'}
                  </div>
                )}
                {l.letter_draft && (
                  <details className="mt-1">
                    <summary className="muted small" style={{ cursor: 'pointer' }}>
                      Letter {l.letter_sent_at ? `(sent ${fmtDate(l.letter_sent_at)})` : '(drafted)'}
                    </summary>
                    <div className="small">
                      <strong>Subject:</strong> {l.letter_subject ?? '—'}
                    </div>
                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                      {l.letter_draft}
                    </pre>
                  </details>
                )}
              </div>
              <div className="row gap-2">
                {l.qualifies && !l.letter_draft && (
                  <button
                    className="btn-primary small"
                    onClick={() => draftLetter(l.id)}
                    disabled={busyId === l.id}
                  >
                    {busyId === l.id ? 'Drafting…' : 'Draft letter'}
                  </button>
                )}
                {l.letter_draft && !l.letter_sent_at && (
                  <button
                    className="btn-primary small"
                    onClick={() => sendLetter(l.id)}
                    disabled={busyId === l.id}
                  >
                    {busyId === l.id ? 'Sending…' : 'Send via Gmail'}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </section>
  )
}
